import * as Sentry from '@sentry/nextjs';

import { ApiError } from '@cineamo/api/types/ApiErrorResponse.types';

import { isReportableApiFailure, setSwallowedErrorObserver } from '@cineamo/api/helpers/api-fallback';
import { createReportThrottle } from '@cineamo/api/helpers/report-throttle';

/** One Sentry event per failing route and kind per minute per JS realm: a polling query or a hard outage is one signal, not thousands. */
const throttle = createReportThrottle(60_000);

/** Set on a query's or mutation's `meta` by code that already reports its own failure, so the global net stays quiet. */
type ReportsOwnErrors = { reportsOwnErrors?: boolean };

/** What an ApiError (or the plain copy `orError` hands out) says about where it came from, else `undefined`. */
function apiOrigin(error: unknown) {
    const e = error as { method?: unknown; route?: unknown; status?: unknown } | null;
    if (typeof e?.method === 'string' && typeof e.route === 'string' && typeof e.status === 'number') {
        return { method: e.method, route: e.route, statusClass: `${Math.floor(e.status / 100)}xx` };
    }
    return undefined;
}

/**
 * Every ApiError is constructed at the same frame, so Sentry's stack grouping would fold them into about one issue
 * per HTTP method. Fingerprint by method, route and status class instead. Network and timeout failures keep
 * Sentry's default grouping (their stacks differ). Returns `null` when the throttle has already let one through.
 */
function report(
    error: unknown
): { fingerprint?: string[]; tags: Record<string, string>; extra: Record<string, unknown> } | null {
    const origin = apiOrigin(error);
    const key = origin
        ? `${origin.method} ${origin.route} ${origin.statusClass}`
        : `${error instanceof Error ? error.name : typeof error}: ${error instanceof Error ? error.message : ''}`;
    if (!throttle.shouldReport(key)) return null;
    const status = (error as { status?: unknown } | null)?.status;
    return {
        ...(origin && { fingerprint: ['api-error', origin.method, origin.route, origin.statusClass] }),
        tags: typeof status === 'number' ? { api_status: String(status) } : {},
        extra: { requestId: error instanceof ApiError ? error.requestId : undefined }
    };
}

/**
 * The global TanStack `QueryCache`/`MutationCache` `onError` (imperative catch blocks use `reportApiFailure` from `@cineamo/api`): reports incidents (5xx, network, JS errors),
 * not expected outcomes (4xx, abort). A query or mutation that reports its own failure sets
 * `meta: { reportsOwnErrors: true }`.
 */
export function reportQueryError(error: unknown, meta?: ReportsOwnErrors): void {
    if (meta?.reportsOwnErrors === true || !isReportableApiFailure(error)) return;
    const context = report(error);
    if (context) Sentry.captureException(error, context);
}

/** `QueryCache({ onError: reportQueryFailure })` */
export function reportQueryFailure(error: unknown, query: { meta?: ReportsOwnErrors }): void {
    reportQueryError(error, query.meta);
}

/** `MutationCache({ onError: reportMutationFailure })` */
export function reportMutationFailure(
    error: unknown,
    _variables: unknown,
    _context: unknown,
    mutation: { options: { meta?: ReportsOwnErrors } }
): void {
    reportQueryError(error, mutation.options.meta);
}

/** Reports failures the `orNull`/`orFalse`/`orError` fallbacks swallow. Call once per app and runtime. */
export function reportSwallowedApiFailures(runtime: 'client' | 'server'): void {
    setSwallowedErrorObserver((error) => {
        const context = report(error);
        if (!context) return;
        const { tags, ...rest } = context;
        Sentry.captureException(error, {
            ...rest,
            level: 'warning',
            tags: { api_swallowed: 'true', runtime, ...tags }
        });
    });
}
